import {
  AtlasDialogRootProps,
  Button,
  Dialog,
  TextField,
} from "@resource/atlas";
import { useLogEvent } from "analytics";
import DepartmentSelect from "components/DepartmentSelect";
import InputGroup from "components/Input/InputGroup";
import { RefObject } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

export type PostTemplateSettingsDialogFormData = {
  name: string;
  departmentId: string;
};

export type PostTemplateSettingsDialogViewProps = {
  title: string;
  submitLoading?: boolean;
  submitButtonText: string;
  useFormReturn: UseFormReturn<
    PostTemplateSettingsDialogFormData & Record<string, unknown>
  >;
  onSubmit: (data: PostTemplateSettingsDialogFormData) => void;
  dialogState: NonNullable<AtlasDialogRootProps["state"]>;
  allowNonDirtySubmission?: boolean;
  initialFocusRef: RefObject<HTMLInputElement>;
  /** Type passed for logging events */
  analyticsType?: string;
};

export function PostTemplateSettingsDialogView(
  props: PostTemplateSettingsDialogViewProps
) {
  const {
    useFormReturn: {
      control,
      handleSubmit,
      formState: { errors, isDirty, isValid, isSubmitted },
    },
    onSubmit,
    title,
    submitLoading,
    submitButtonText,
    dialogState,
    allowNonDirtySubmission = false,
    initialFocusRef,
    analyticsType,
  } = props;
  const logEvent = useLogEvent({
    component: "PostTemplateSettingsDialogView",
    project: "Post templates",
    ...(analyticsType && { type: analyticsType }),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Dialog.View
        header={{ title }}
        footer={{
          leftActions: (
            <Button
              onClick={() => {
                logEvent("Post Template Settings Dialog Cancelled");
                dialogState.hide();
              }}
            >
              Cancel
            </Button>
          ),
          rightActions: (
            <Button
              onClick={() => {
                logEvent("Post Template Settings Dialog Submitted");
              }}
              type="submit"
              variant="primary"
              disabled={(!allowNonDirtySubmission && !isDirty) || !isValid}
              isLoading={submitLoading}
            >
              {submitButtonText}
            </Button>
          ),
        }}
      >
        <div className="flex flex-col gap-6 mb-2">
          <InputGroup
            label={
              <p>
                Name <span className="text-accent">*</span>
              </p>
            }
            error={isSubmitted ? errors.name : undefined}
            inputField={
              <Controller
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={initialFocusRef}
                    placeholder="Name"
                    inputClassName="rounded-md"
                  />
                )}
                name="name"
                rules={{
                  required: {
                    value: true,
                    message: "Name is required",
                  },
                }}
                control={control}
              />
            }
          />
          <InputGroup
            label="Department"
            error={errors.departmentId}
            inputField={
              <Controller
                render={({ field }) => (
                  <DepartmentSelect
                    setValue={(departmentId) => {
                      field.onChange(departmentId);
                    }}
                    selectedKey={field.value}
                  />
                )}
                name="departmentId"
                control={control}
              />
            }
          />
        </div>
      </Dialog.View>
    </form>
  );
}
